// Initialize Sentry before anything else to capture early errors (admin/support/docs pages)
import "@/shared/lib/sentry-browser"

// Entry point for Hotwire/Turbo/Stimulus
import "@hotwired/turbo-rails"
import * as Turbo from "@hotwired/turbo"
import * as pdfjsLib from "pdfjs-dist"
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url"

import "@javascript/controllers"
import { handleChunkLoadError } from "@shared/lib/chunk-error-reload"
import { installNumberInputWheelGuard } from "@shared/lib/number-input-wheel-guard"
import { turboConfirm } from "@shared/lib/turbo-confirm"

// Scrolling over a focused number input must scroll the page, not step its
// value (CONEK-5346). This entrypoint is loaded by every layout — the Inertia
// one included — so a single document-level install covers React, Hotwire and
// the conek-ui engine's inputs.
installNumberInputWheelGuard(document)

// Handle chunk load errors after rolling deploys (CONEK-2134).
// See entrypoints/inertia.tsx for full explanation.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault()
  handleChunkLoadError()
})

// When Turbo Drive is disabled (data-turbo="false" on <body>), Turbo's popstate
// listener still fires on browser back/forward. It doesn't recognize Inertia's
// encrypted history state ({page: ArrayBuffer}) and calls historyPoppedWithEmptyState(),
// which replaces the entry with a Turbo-only state — destroying Inertia's page data.
// This causes the *next* back navigation to fail (stale content, recovery visit).
// Removing the listener is safe: Turbo Frames, Stimulus, and turbo_confirm are unaffected.
if (document.body.dataset.turbo === "false") {
  window.removeEventListener("popstate", Turbo.session.history.onPopState)
}

// Custom Turbo Stream action: `calendar_reload` (CONEK-4230).
// The Hotwire calendar (v2) renders each event as an absolutely-positioned card
// whose top/height is computed server-side, so a real-time single-chip insert
// cannot be positioned on the client — it would land unstyled at the bottom of
// the scroll container. Instead, any booking mutation (create/edit/drag/delete
// via BookingsController, or an MS Graph webhook echo) emits this action; the
// pipeline--calendar controller listens for `calendar:reload` and re-fetches the
// current view so the server re-renders every card in its correct position.
// The action's `target` is inert — every reload re-fetches the whole grid, so
// callers pass "calendar_events" only to satisfy the turbo-stream tag contract.
Turbo.StreamActions.calendar_reload = function () {
  document.dispatchEvent(new CustomEvent("calendar:reload"))
}

// Custom Turbo Stream action: `frame_reload` (CONEK-7252).
// Re-executes a lazy Turbo Frame's own request, so the frame's server side
// re-renders for whoever is looking at it. This is how a leg's event history
// follows a device in real time: the payload would otherwise have to be a
// rendered fragment, and a fragment cannot carry what only the viewer's own
// request can decide — the per-event links, the report link, the inspection
// button. Reloading keeps all of it, at the cost of one request.
// The guard is what makes it safe to emit blind: a page that has no such frame
// (a different page, or the phone's full-history screen, which pages by hand
// and must NOT lose the pages the reader already loaded) simply ignores it.
// Frames with no `src` are skipped too — a frame rendered inline has nothing
// to re-fetch, and `reload()` on it would blank it.
Turbo.StreamActions.frame_reload = function () {
  const frame = document.getElementById(this.target)
  if (frame && typeof frame.reload === "function" && frame.getAttribute("src"))
    frame.reload()
}

// Override native confirm() with a styled dialog for turbo_confirm.
// Variants, per-call title/button overrides and the translated defaults are
// documented in shared/lib/turbo-confirm.ts.
Turbo.config.forms.confirm = turboConfirm

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

// Re-initialize Lexxy editors inserted via Turbo Frame navigation.
// When Turbo replaces a frame's content, custom elements fire connectedCallback
// but Lexxy's init can fail mid-flight. Moving the element in the DOM
// forces a clean disconnect → reconnect cycle once the frame is fully rendered.
function reinitializeLexxyEditors() {
  document.querySelectorAll("lexxy-editor").forEach((editor) => {
    if (!editor.querySelector("[contenteditable], [role='textbox']")) {
      const parent = editor.parentNode
      if (!parent) return
      const next = editor.nextSibling
      parent.removeChild(editor)
      parent.insertBefore(editor, next)
    }
  })
}

document.addEventListener("turbo:frame-load", () => {
  // Two passes: rAF catches most cases; 300ms catches slow custom element upgrades
  requestAnimationFrame(reinitializeLexxyEditors)
  setTimeout(reinitializeLexxyEditors, 300)
})

// Defensive cleanup for stuck body scroll-lock on Turbo navigations.
//
// Several /docs Stimulus controllers (search, lightbox, youtube) lock the body
// while their modal/lightbox is open by setting `body.style.overflow = "hidden"`.
// They reset it on close — but if a user clicks a search result *while the modal
// is still open*, Turbo Drive navigates and snapshots the page WITH the inline
// style intact. When the user later hits Back, Turbo restores that snapshot and
// the body is mounted with `overflow:hidden` even though no modal is open →
// the page becomes unscrollable. This is the intermittent "sometimes I can't
// scroll" bug on /docs.
//
// The docs--layout controller uses a different locking mechanism: it shows a
// full-viewport overlay (fixed inset-0 z-40) by removing the `hidden` class.
// The controller's own connect()/disconnect() handles this, but this handler
// is a second line of defense in case the controller isn't connected (e.g.
// the element hasn't upgraded yet when turbo:before-cache fires).
//
// Fix: clear the inline overflow style AND reset the docs sidebar overlay
// right before Turbo caches a page, so snapshots never carry the lock forward.
document.addEventListener("turbo:before-cache", () => {
  if (document.body.style.overflow === "hidden") {
    document.body.style.overflow = ""
  }

  // Close any open native <dialog> (Ui::ModalComponent). A snapshot-restored
  // `<dialog open>` loses its top-layer state and renders in-flow — framing the
  // page in a leftover popup box after a Turbo Back. (CONEK-6282)
  document.querySelectorAll("dialog[open]").forEach((dialog) => dialog.close())

  // ...and clear each modal's open-value so the reset STICKS — exactly like the
  // drawer reset below. modal_controller.js#connect() re-opens whenever open-value
  // is "true", so a cached snapshot (shown as a navigation preview or restored on
  // Back) would otherwise flash the modal open for a moment before the fresh
  // render clears it — visible when opening a booking's detail/create modal, then
  // switching calendar view or navigating. (CONEK-4230)
  document
    .querySelectorAll("dialog[data-controller~='ui--modal']")
    .forEach((dialog) =>
      dialog.setAttribute("data-ui--modal-open-value", "false"),
    )

  // Reset any open drawer (Ui::DrawerComponent) to its closed DOM state, mirroring
  // drawer_controller.js#close(): hide the root, slide the panel out, and clear the
  // open-value. Clearing open-value is what makes the reset stick — drawer_controller
  // .js#connect() re-show()s whenever open-value is "true", so a restored `open: true`
  // drawer would otherwise reopen on Back (a user-opened drawer already carries
  // open-value "false"). (CONEK-6282)
  const DRAWER_SLIDE_OUT = {
    left: "-translate-x-full",
    right: "translate-x-full",
    top: "-translate-y-full",
    bottom: "translate-y-full",
  }
  document
    .querySelectorAll("[data-controller~='ui--drawer']:not(.hidden)")
    .forEach((drawer) => {
      drawer.classList.add("hidden")
      drawer.setAttribute("data-ui--drawer-open-value", "false")
      const panel = drawer.querySelector("[data-ui--drawer-target='panel']")
      const position = drawer.getAttribute("data-ui--drawer-position-value")
      panel?.classList.add(DRAWER_SLIDE_OUT[position] || DRAWER_SLIDE_OUT.right)
    })

  const docsOverlay = document.querySelector(
    "[data-docs--layout-target='overlay']",
  )
  if (docsOverlay && !docsOverlay.classList.contains("hidden")) {
    docsOverlay.classList.add("hidden")
    const docsSidebar = document.querySelector(
      "[data-docs--layout-target='sidebar']",
    )
    if (docsSidebar) {
      docsSidebar.classList.add("-translate-x-full")
      docsSidebar.classList.remove("translate-x-0")
    }
  }
})
